/**
 * VerbChip — the tone-coded verb pill used by SuggestionRow,
 * EvidenceSheet, ActivityPage rows, and any other surface
 * that needs to label a Suggestion/Vote/ExecutedAction by verb.
 *
 * Reuses the VERB_TONES table from EvidenceSheet/tones.ts so the
 * pill chrome stays identical across surfaces.
 */
import React from 'react';
import {Box} from '@mui/material';
import {SuggestionVerb} from '../../types/generated/graphql';
import {VERB_TONES, verbTokenForVerb} from './EvidenceSheet/tones';

export interface VerbChipProps {
  verb: SuggestionVerb;
  /**
   * Which label to display:
   *   - 'imperative' (default): "Block" / "Follow" / "Boost" / "Skip"
   *     — for Review + Activity → Pending + Activity → My decisions
   *   - 'past': "Blocked" / "Followed" / "Boosted" / "Skipped"
   *     — for Activity → On YouTube only (executed actions)
   */
  tense?: 'imperative' | 'past';
  /** Slightly larger padding/font for in-row prominence. */
  big?: boolean;
  /**
   * Pill chrome to use:
   *   - 'dark' (default): pale text on a tinted dark fill — for the
   *     Review surface (slate-950 bg).
   *   - 'light': saturated text on a {tone}-50 fill — for the
   *     Activity surface (white/slate-50 bg). The dark triple reads as
   *     ghost-text on white, which is why SKIP and BLOCK appeared
   *     empty/faded before this prop existed.
   */
  palette?: 'dark' | 'light';
}

export function VerbChip({
  verb,
  tense = 'imperative',
  big = false,
  palette = 'dark',
}: VerbChipProps): React.JSX.Element {
  const t = VERB_TONES[verbTokenForVerb(verb)];
  const label = tense === 'past' ? t.pastLabel : t.actionLabel;
  const chrome =
    palette === 'light'
      ? {bg: t.pillBgLight, border: t.pillBorderLight, text: t.pillTextLight}
      : {bg: t.pillBg, border: t.pillBorder, text: t.pillText};
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: `1px solid ${chrome.border}`,
        backgroundColor: chrome.bg,
        color: chrome.text,
        borderRadius: '4px',
        px: big ? '8px' : '6px',
        py: '2px',
        // Uniform footprint so the four labels (BLOCK/FOLLOW/BOOST/SKIP)
        // line up across a list. Without min-width the shorter labels
        // collapse and the rows look ragged.
        minWidth: big ? '64px' : '56px',
        textAlign: 'center',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        fontSize: big ? '10px' : '9px',
        lineHeight: 1.2,
        flexShrink: 0,
      }}
    >
      {label}
    </Box>
  );
}
